import type { FC } from 'react';
import React from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import type { ReceiptAttachment } from '../utils/getImagesFromReceiptIds';

interface ResidentPdfExportModalViewProps {
  onClose: () => void;
  onExport: () => void;
  onLastMonth: () => void;
  onLastYear: () => void;

  // Form state
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;

  includeReceiptImages: boolean;
  onIncludeReceiptImagesChange: (checked: boolean) => void;

  includeCancelledReceiptAttachments: boolean;
  onIncludeCancelledReceiptAttachmentsChange: (checked: boolean) => void;

  // State
  isLoadingTransactions: boolean;
  preparingPDF: boolean;
  receiptPdfsLoading: boolean;
  isImageLoading: boolean;

  // Configuration
  residentCreatedDate: Date;
  lastMonthButtonActive: boolean;
  lastYearButtonActive: boolean;
  residentCreatedDateOlderThanCurrentMonth: boolean;
  residentCreatedDateOlderThanCurrentYear: boolean;
  hasCancelledReceipts: boolean;
  showCancelledReceiptsCheckbox?: boolean;

  // Data
  pdfFiles: ReceiptAttachment[];

  // Validation
  dateError?: string;

  // Text labels
  title: string;
  pleaseSelectRange: string;
  fromLabel: string;
  toLabel: string;
  lastMonthLabel: string;
  lastYearLabel: string;
  receiptOptionsLabel: string;
  includeReceiptImagesLabel: string;
  includeCancelledReceiptAttachmentsLabel: string;
  cancelLabel: string;
  exportLabel: string;
}

export const ResidentPdfExportModalView: FC<
  ResidentPdfExportModalViewProps
> = ({
  onClose,
  onExport,
  onLastMonth,
  onLastYear,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  includeReceiptImages,
  onIncludeReceiptImagesChange,
  includeCancelledReceiptAttachments,
  onIncludeCancelledReceiptAttachmentsChange,
  isLoadingTransactions,
  preparingPDF,
  receiptPdfsLoading,
  isImageLoading,
  residentCreatedDate,
  lastMonthButtonActive,
  lastYearButtonActive,
  residentCreatedDateOlderThanCurrentMonth,
  residentCreatedDateOlderThanCurrentYear,
  hasCancelledReceipts,
  showCancelledReceiptsCheckbox,
  pdfFiles,
  dateError,
  title,
  pleaseSelectRange,
  fromLabel,
  toLabel,
  lastMonthLabel,
  lastYearLabel,
  receiptOptionsLabel,
  includeReceiptImagesLabel,
  includeCancelledReceiptAttachmentsLabel,
  cancelLabel,
  exportLabel,
}) => {
  return (
    <Box>
      <Card sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ mb: 1 }}>{pleaseSelectRange}:</Typography>
        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <DatePicker
            label={fromLabel}
            value={dateFrom}
            onChange={onDateFromChange}
            minDate={residentCreatedDate}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField
                label={fromLabel}
                {...params}
                helperText={dateError}
                error={dateError ? true : false}
              />
            )}
          />
          <DatePicker
            label={toLabel}
            value={dateTo}
            onChange={onDateToChange}
            minDate={residentCreatedDate}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField
                label={toLabel}
                {...params}
                error={dateError ? true : false}
              />
            )}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-start"
          gap={1}
          sx={{
            mt: 1,
            mb: 3,
          }}
        >
          <Button
            disabled={!residentCreatedDateOlderThanCurrentMonth}
            onClick={onLastMonth}
            variant={lastMonthButtonActive ? 'contained' : 'outlined'}
            size="small"
          >
            {lastMonthLabel}
          </Button>
          <Button
            disabled={!residentCreatedDateOlderThanCurrentYear}
            onClick={onLastYear}
            variant={lastYearButtonActive ? 'contained' : 'outlined'}
            size="small"
          >
            {lastYearLabel}
          </Button>
        </Stack>
        <Typography>{receiptOptionsLabel}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeReceiptImages}
              onChange={(e, checked) => onIncludeReceiptImagesChange(checked)}
            />
          }
          label={includeReceiptImagesLabel}
        />
        {includeReceiptImages &&
          hasCancelledReceipts &&
          showCancelledReceiptsCheckbox && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCancelledReceiptAttachments}
                  onChange={(e, checked) =>
                    onIncludeCancelledReceiptAttachmentsChange(checked)
                  }
                />
              }
              label={includeCancelledReceiptAttachmentsLabel}
            />
          )}

        <DownloadPdfReceiptsView
          pdfFiles={pdfFiles}
          loading={receiptPdfsLoading}
        />

        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <Button
            variant="outlined"
            disabled={preparingPDF || receiptPdfsLoading}
            onClick={onClose}
          >
            {cancelLabel}
          </Button>

          <LoadingButton
            color="primary"
            variant="contained"
            loading={
              preparingPDF ||
              isLoadingTransactions ||
              isImageLoading ||
              receiptPdfsLoading
            }
            disabled={
              preparingPDF || Boolean(dateError) || !dateFrom || !dateTo
            }
            onClick={onExport}
          >
            {exportLabel}
          </LoadingButton>
        </Stack>
      </Card>
    </Box>
  );
};

interface DownloadPdfReceiptsViewProps {
  pdfFiles: ReceiptAttachment[];
  loading: boolean;
  pdfFilesLabel?: string;
  receiptNumberLabel?: string;
  downloadPdfLabel?: string;
}

const DownloadPdfReceiptsView: FC<DownloadPdfReceiptsViewProps> = ({
  pdfFiles,
  loading,
  pdfFilesLabel = 'PDF files',
  receiptNumberLabel = 'Receipt {{number}}',
  downloadPdfLabel = 'Download PDF',
}) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton height={50} />;
  }

  if (pdfFiles.length === 0) {
    return null;
  }

  const receiptCountMap = pdfFiles.reduce((acc, file) => {
    const count = acc.get(file.sequenceNumber) || 0;
    acc.set(file.sequenceNumber, count + 1);
    return acc;
  }, new Map<number, number>());

  return (
    <Stack sx={{ mt: 0.5 }}>
      <Typography variant="body1" sx={{ mb: 0.1 }}>
        {pdfFilesLabel}:
      </Typography>

      {pdfFiles.map((file) => (
        <Stack direction="row" gap={1} key={file.image.uri}>
          <Typography variant="body2">
            {receiptNumberLabel.replace(
              '{{number}}',
              (receiptCountMap.get(file.sequenceNumber) ?? 0) > 1
                ? `${file.sequenceNumber}-${file.count}`
                : `${file.sequenceNumber}`
            )}
            :
          </Typography>

          <Typography variant="body2" fontWeight="bold">
            <a
              href={file.image.uri}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: 'none',
                color: theme.palette.primary.main,
              }}
            >
              {downloadPdfLabel}
            </a>
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
